import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Button } from "../../components/Button";
import { OtpInput } from "../../components/OtpInput";
import { register, requestOtp, verifyOtp } from "../../api/auth";
import { ApiError } from "../../api/client";
import { readConsents, type SignupConsents } from "./ConsentScreen";

const RESEND_SECONDS = 24;

/**
 * Screen 3 — the code checks itself.
 *
 * The code is always sent to email — no channel picker, no SMS fallback.
 * Auto-submit on 6th digit. The resend button sends another email.
 */
export function OtpVerifyScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const handoff = (location.state ?? {}) as {
    email?: string;
    resendAfterSeconds?: number;
    password?: string;
    nickname?: string;
    /** From the consent screen, the first step of sign-up. */
    consents?: SignupConsents;
    /** "reset" when arriving from the forgot-password flow. */
    purpose?: "reset" | undefined;
  };
  const email = handoff.email ?? "";

  const [code, setCode] = useState("");
  const [failures, setFailures] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(handoff.resendAfterSeconds ?? RESEND_SECONDS);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setTimeout(() => setSecondsLeft((left) => left - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const verify = async (entered: string) => {
    if (checking) return;
    setChecking(true);
    setError(null);

    try {
      // Password reset: check the code (it isn't used up yet), then move to the
      // new-password screen, which sends it again with the new password.
      if (handoff.purpose === "reset") {
        await verifyOtp(email, entered, "password_reset");
        navigate("/reset/new-password", { state: { email, code: entered } });
        return;
      }

      // Signup: register in one call so the code is verified and the account
      // created together.
      if (handoff.password) {
        const consents = handoff.consents ?? readConsents();
        await register({
          email,
          code: entered,
          password: handoff.password,
          ...(handoff.nickname ? { firstName: handoff.nickname } : {}),
          acceptedTerms: consents?.acceptedTerms === true,
          marketingEmailOptIn: consents?.marketingEmailOptIn === true,
        });
        navigate("/age", { state: { email } });
        return;
      }

      // No password: an existing student proving possession.
      const result = await verifyOtp(email, entered, "signup");
      navigate(result.registered ? "/home" : "/age", { state: { email } });
    } catch (caught) {
      setFailures((count) => count + 1);
      setError(
        caught instanceof ApiError ? caught.message : "Couldn't check that code. Try again.",
      );
      setCode("");
    } finally {
      setChecking(false);
    }
  };

  const resend = async () => {
    setError(null);
    setCode("");

    try {
      // The same kind of code as the first: a resent reset code must still
      // be a reset code, or the new password is refused.
      const { resendAfterSeconds } = await requestOtp(email, {
        purpose: handoff.purpose === "reset" ? "password_reset" : "signup",
      });
      setSecondsLeft(resendAfterSeconds);
      setFailures(0);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Couldn't resend. Try again.");
    }
  };

  // Mask the email: show first 2 chars + *** + domain
  const maskedEmail = email ? `${email.slice(0, 2)}***@${email.split("@")[1] ?? ""}` : "";

  return (
    <div className="app-column px-5 pb-6 pt-6">
      <div className="flex flex-col gap-1 pt-6">
        <h1 className="m-0 font-[family-name:var(--font-display)] text-[26px] font-bold leading-[1.15]">
          Check your email.
        </h1>
        {maskedEmail ? (
          <p className="m-0 text-[13.5px] text-text-muted">
            We sent a 6-digit code to <span className="text-text">{maskedEmail}</span>
          </p>
        ) : null}
      </div>

      <div className="pt-6">
        <OtpInput
          value={code}
          onChange={setCode}
          onComplete={(entered) => void verify(entered)}
          invalid={Boolean(error)}
          disabled={checking}
        />
      </div>

      {error ? (
        <p role="alert" className="m-0 pt-3 text-[12.5px] leading-[1.5] text-negative">
          {error}
        </p>
      ) : (
        <p className="m-0 pt-3 text-[12.5px] text-text-muted">
          The code fills in and checks itself — no button needed.
        </p>
      )}

      <div className="pt-5">
        {secondsLeft > 0 ? (
          <p className="m-0 text-center text-[13px] text-text-muted">
            Resend in <span className="tabular">0:{String(secondsLeft).padStart(2, "0")}</span>
          </p>
        ) : (
          <Button onClick={() => void resend()}>Send code again</Button>
        )}
      </div>

      {failures >= 2 ? (
        <div className="mt-4 flex flex-col gap-2 rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <p className="m-0 text-[13px] font-semibold">Code not arriving?</p>
          <p className="m-0 text-[12.5px] text-text-muted">
            Check your spam or junk folder. The email comes from PrepAI.
          </p>
        </div>
      ) : null}

      <p className="m-0 mt-auto pt-8 text-center text-[13px] text-text-muted">
        Wrong email?{" "}
        <button
          type="button"
          onClick={() => void navigate(-1)}
          className="font-semibold text-accent"
        >
          Change it
        </button>
      </p>
    </div>
  );
}
